import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, CancelTokenSource } from 'axios';
import { config as appConfig } from '../config/config';
import { decodeJWT } from './auth-service';

// API Error types
export interface ApiError {
    status: number;
    message: string;
    code?: string;
    data?: any;
}

// Request configuration
const apiConfig: AxiosRequestConfig = {
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json'
    }
};

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

// Create a custom event for server errors
export const SERVER_ERROR_EVENT = 'server-error';

// Create API client instance
export class ApiClient {
    private client: AxiosInstance;
    private cancelTokenSource: CancelTokenSource | null = null;

    constructor() {
        this.client = axios.create({
            ...apiConfig,
            baseURL: appConfig.api.baseUrl
        });

        this.setupInterceptors();
    }

    private setupInterceptors(): void {
        // Request interceptor - Add JWT token to headers
        this.client.interceptors.request.use(
            (config) => {
                // Skip Authorization header for public endpoints (validate-token, send-otp, validate-otp)
                // These endpoints don't require JWT - they generate it
                const publicEndpoints = ['/portal/validate-token', '/portal/send-otp', '/portal/validate-otp'];
                const isPublicEndpoint = publicEndpoints.some(endpoint => config.url?.includes(endpoint));

                // Only add Authorization header for protected endpoints
                if (!isPublicEndpoint) {
                    const jwt = localStorage.getItem('portal_jwt');
                    if (jwt) {
                        config.headers.Authorization = `Bearer ${jwt}`;
                    }
                } else {
                    // Explicitly remove Authorization header for public endpoints
                    delete config.headers.Authorization;
                    // Log that we're skipping auth for public endpoint
                    if (appConfig.enableLogging) {
                        console.log(`[PUBLIC ENDPOINT] Skipping Authorization header for: ${config.url}`);
                    }
                }

                // Log request in development
                if (appConfig.enableLogging) {
                    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`, {
                        url: config.url,
                        baseURL: config.baseURL,
                        headers: config.headers,
                        data: config.data
                    });
                }

                return config;
            },
            (error) => {
                console.error('Request interceptor error:', error);
                return Promise.reject(error);
            }
        );

        // Response interceptor - Handle errors and token expiry
        this.client.interceptors.response.use(
            (response: AxiosResponse) => {
                // Log response in development
                if (appConfig.enableLogging) {
                    console.log(`API Response: ${response.status} ${response.config.url}`, response.data);
                }

                return response;
            },
            async (error) => {
                const originalRequest = error.config;

                // Handle 401 Unauthorized (token expiry)
                if (error.response?.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true;

                    // Create detailed error log object
                    const errorLog = {
                        timestamp: new Date().toISOString(),
                        url: error.config?.url,
                        method: error.config?.method,
                        status: error.response?.status,
                        statusText: error.response?.statusText,
                        responseData: error.response?.data,
                        requestHeaders: error.config?.headers,
                        fullError: error.toString(),
                    };

                    // Log detailed error information for debugging
                    console.error('=== 401 UNAUTHORIZED ERROR ===');
                    console.error('Timestamp:', errorLog.timestamp);
                    console.error('URL:', errorLog.url);
                    console.error('Method:', errorLog.method);
                    console.error('Status:', errorLog.status);
                    console.error('Status Text:', errorLog.statusText);
                    console.error('Response Data:', errorLog.responseData);
                    console.error('Request Headers:', errorLog.requestHeaders);
                    console.error('Full Error:', error);
                    console.error('==============================');

                    // Store error in localStorage for persistence (last 5 errors)
                    try {
                        const storedErrors = JSON.parse(localStorage.getItem('portal_api_errors') || '[]');
                        storedErrors.unshift(errorLog);
                        // Keep only last 5 errors
                        if (storedErrors.length > 5) {
                            storedErrors.pop();
                        }
                        localStorage.setItem('portal_api_errors', JSON.stringify(storedErrors));
                        console.error('Error saved to localStorage. Check localStorage.getItem("portal_api_errors")');
                    } catch (e) {
                        console.error('Failed to save error to localStorage:', e);
                    }

                    // Extract clientId from JWT before clearing it
                    const jwt = localStorage.getItem('portal_jwt');
                    let clientId = null;
                    if (jwt) {
                        try {
                            const decoded = decodeJWT(jwt);
                            if (decoded) {
                                clientId = decoded?.clientId || null;
                                console.error('JWT Decoded - ClientId:', clientId, 'Exp:', decoded.exp, 'Current Time:', Math.floor(Date.now() / 1000));
                            }
                        } catch (error) {
                            console.error('Failed to decode JWT:', error);
                            // Failed to decode JWT, continue without clientId
                        }
                    } else {
                        console.error('No JWT found in localStorage');
                    }

                    // Check if redirects are disabled for debugging
                    const disableRedirects = localStorage.getItem('portal_disable_redirects') === 'true';

                    if (disableRedirects) {
                        console.error('⚠️ REDIRECTS DISABLED FOR DEBUGGING ⚠️');
                        console.error('To enable redirects, run: localStorage.removeItem("portal_disable_redirects")');
                        // Show alert with error details
                        alert(`401 Error - Redirects Disabled\n\nURL: ${errorLog.url}\nStatus: ${errorLog.status}\nResponse: ${JSON.stringify(errorLog.responseData, null, 2)}\n\nCheck console and localStorage.getItem("portal_api_errors") for details.`);
                        return Promise.reject(error);
                    }

                    // Clear invalid JWT
                    localStorage.removeItem('portal_jwt');

                    // Store clientId for OTP flow if available
                    if (clientId) {
                        localStorage.setItem('portal_client_id', clientId.toString());
                    }

                    // Add a longer delay to allow console logs to be visible
                    console.error('⚠️ Redirecting to login in 10 seconds...');
                    console.error('To disable redirects for debugging, run: localStorage.setItem("portal_disable_redirects", "true")');
                    setTimeout(() => {
                        // Redirect to login page
                        window.location.href = '/login';
                    }, 10000); // 10 second delay to see logs

                    return Promise.reject(error);
                }

                // Handle other errors
                if (error.response) {
                    const apiError: ApiError = {
                        status: error.response.status,
                        message: error.response.data?.error || error.message,
                        code: error.response.data?.errorType,
                        data: error.response.data
                    };

                    // Log error in development
                    if (appConfig.enableLogging) {
                        console.error('API Error:', apiError);
                    }

                    // Dispatch custom error event
                    window.dispatchEvent(new CustomEvent(SERVER_ERROR_EVENT, { detail: apiError }));

                    return Promise.reject(apiError);
                }

                // Network error
                const networkError: ApiError = {
                    status: 0,
                    message: 'Network error - please check your connection',
                    code: 'NETWORK_ERROR'
                };

                console.error('Network Error:', networkError);
                return Promise.reject(networkError);
            }
        );
    }

    // Generic request method with retry logic
    private async request<T>(config: AxiosRequestConfig & { _skipRetry?: boolean }, retryCount = 0): Promise<T> {
        try {
            const response = await this.client.request<T>(config);
            return response.data;
        } catch (error) {
            // Skip retry if explicitly disabled (e.g., for POST requests that create data)
            if (config._skipRetry) {
                throw error;
            }
            // Retry logic for network errors
            if (retryCount < MAX_RETRIES && this.shouldRetry(error)) {
                console.log(`Retrying request (${retryCount + 1}/${MAX_RETRIES})...`);
                await this.delay(RETRY_DELAY * (retryCount + 1));
                return this.request<T>(config, retryCount + 1);
            }
            throw error;
        }
    }

    private shouldRetry(error: any): boolean {
        // Retry on network errors or 5xx server errors
        return (
            !error.response || // Network error
            (error.response.status >= 500 && error.response.status < 600) // Server error
        );
    }

    private delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    // HTTP Methods
    async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        return this.request<T>({ ...config, method: 'GET', url });
    }

    async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        return this.request<T>({ ...config, method: 'POST', url, data });
    }

    async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        return this.request<T>({ ...config, method: 'PUT', url, data });
    }

    async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        return this.request<T>({ ...config, method: 'DELETE', url });
    }

    // Cancel ongoing requests
    cancelRequests(): void {
        if (this.cancelTokenSource) {
            this.cancelTokenSource.cancel('Request cancelled by user');
        }
    }

    // Create new cancel token
    createCancelToken(): CancelTokenSource {
        this.cancelTokenSource = axios.CancelToken.source();
        return this.cancelTokenSource;
    }

    // Get the underlying axios instance (for advanced usage)
    getAxiosInstance(): AxiosInstance {
        return this.client;
    }
}

// Create and export a singleton instance
export const apiClient = new ApiClient();

// Export default
export default apiClient;
